import { CheckCircleOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import {
  Col,
  Checkbox,
  Collapse,
  Form,
  Input,
  Row,
  Space,
  Tooltip,
  Transfer,
  TransferProps,
} from 'antd';
import { ReactComponent as IconFuzzyMapping } from 'assets/images/icons/iconFuzzyMapping.svg';
import { ReactComponent as IconImport } from 'assets/images/icons/iconImport.svg';
import { ReactComponent as IconSupport } from 'assets/images/icons/iconSupport.svg';
import TifdMappingStepOne from 'assets/images/tifd-mapping-step-1.svg';
import TifdMappingStepActivities from 'assets/images/tifd-mapping-step-activities.svg';
import TifdMappingStepTwo from 'assets/images/tifd-mapping-step-2.svg';
import TifdMappingStepThree from 'assets/images/tifd-mapping-step-3.svg';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { FeatureDotBadge } from 'components/Custom/FeatureDotBadge';
import { formatDisplayDate } from 'i18n/calendarLabels';
import { getMappingSectionLabel } from 'i18n/mappingSectionLabel';
import moment from 'moment';
import { SelectedRate, StepOneData, TaxRateOption } from 'pages/returns';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Provider } from 'store/api/enums';
import { turnModalOn } from 'store/app/actions';
import { TaxRateType, WindowType } from 'store/app/enums';
import { ModalData } from 'store/app/types';
import { MappingData } from 'store/returns/models';
import { Section, TaxRate } from 'store/returns/models/returnData';
import {
  TIFD_ACTIVITY_FIELD_MAX,
  TIFD_ACTIVITY_LIMIT,
  TifdActivityDraft,
  createTifdActivityDraft,
  tifdActivityText,
  writeTifdActivityDrafts,
} from 'store/returns/models/mappingTaxRate';
import styled from 'styled-components';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
} from 'utils/preferences';
import { resolveProviderIconSrc } from 'utils/resolve-provider-icon-src';
import {
  getMappedSubsectionCodesFromSelectedRates,
  isMappingSectionCompleted,
  isOssReturnType,
} from 'utils/oss-mapping';
import { isMaltaTifdReturnType } from 'utils/tifd-mapping';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { MappingBoxSelectRow } from './mapping-box-select-row';
import { MappingGuide, MappingGuideStep } from './mapping-guide';

interface RecordType {
  key: string;
  title: string;
  description: string;
}

const MapTaxRatesWrapper = styled.div`
  font-family: var(--font-family, Inter, sans-serif);
  margin-bottom: 50px;
  .box-column {
    width: 100px;
  }
  .box-column-space {
    width: 5px;
  }

  .mapping-label {
    font-size: 12px;
    font-weight: 400;
    font-family: var(--font-family, Inter, sans-serif);
    text-align: left;
    color: var(--color-text-base, #2e2e2e);
    display: flex;
    gap: 5px;
    margin-bottom: 5px;
  }

  .mapping-label span:hover {
    color: var(--color-primary, #41aaaf);
  }

  .mapping-section {
    font-size: 22px;
    font-weight: 500;
    font-family: var(--font-family, Inter, sans-serif);
    line-height: 24px;
    text-align: left;
    margin-bottom: 35px;
    display: flex;
    justify-content: space-between;
    color: var(--color-text-base, #2e2e2e);
  }

  .system-generated {
    height: 45px;
    border-radius: 2px;
    background: var(--color-layout-bg, #e9f6f7);
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--color-text-muted, #5a6a6a);
    font-size: 14px;
    font-weight: 400;
    font-family: var(--font-family, Inter, sans-serif);
    padding: 0 25px;
    width: 100%;

    i,
    em {
      font-family: var(--font-family, Inter, sans-serif);
      font-style: italic;
    }
  }

  .mapping-box,
  .mapping-box-oos,
  .mapping-box-other {
    min-width: 80px;
    height: 45px;
    border-radius: 2px;
    background: var(--color-status-warning-bg, #fff5e2);
    border: 1px solid var(--color-border, #dbeae3);
    font-size: 12px;
    font-weight: 600;
    font-family: var(--font-family, Inter, sans-serif);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
  }

  .mapping-box-oos {
    background: #ffe9e2;
  }

  .mapping-box-other {
    background: #e2eeff;
  }
  .oos-link {
    font-size: 14px;
    font-weight: 800;
    font-family: var(--font-family, Inter, sans-serif);
    color: #41aaaf;
  }

  .ant-transfer-list-content {
    background: var(--color-surface, #f3f7f8);
  }
  .ant-transfer-list-header {
    background: #41aaaf1a;
  }
  .ant-transfer-list {
    border: 1px solid var(--color-border, #dbeae3);
  }
  .ant-transfer-list-body {
    background: var(--color-surface, #f3f7f8);
  }
  .ant-transfer-list-header-title {
    font-size: 18px;
    font-weight: 600;
    font-family: var(--font-family, Inter, sans-serif);
  }
  .ant-empty-image {
    display: none;
  }
  .ant-select-selection-placeholder {
    top: 65% !important;
  }

  .mapping-details-box {
    border-radius: 10px;
    border: 1px solid var(--color-border, #dbeae3);
    background: var(--color-surface, #f3f7f8);
    padding: 20px;
    margin-bottom: 40px;
  }

  .mapping-activities-collapse {
    margin-bottom: 24px;
    background: transparent;
    border: none;

    > .ant-collapse-item {
      background: var(--color-surface, #f3f7f8);
      border: 1px solid var(--color-border, #dbeae3) !important;
      border-radius: 10px !important;
      overflow: hidden;
    }

    .ant-collapse-header {
      padding: 20px !important;
      align-items: center !important;
    }

    .ant-collapse-content-box {
      padding: 0 20px 20px !important;
    }

    .ant-collapse-header,
    .ant-collapse-content,
    .ant-collapse-content-box {
      background: transparent !important;
    }

    .ant-collapse-item-disabled > .ant-collapse-header {
      cursor: default !important;
      color: inherit !important;
      opacity: 1 !important;
    }

    .ant-collapse-expand-icon {
      color: var(--color-text-muted, #5a6a6a);
    }
  }

  .mapping-activities-header {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
    padding-right: 8px;
  }

  .mapping-activities-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: var(--color-text-base, #2e2e2e);
  }

  .mapping-activities-names {
    min-width: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: var(--color-text-base, #2e2e2e);
  }

  .mapping-activities-description {
    margin: 0 0 16px;
    max-width: 720px;
    font-size: 13px;
    line-height: 20px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .mapping-activity-row {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 16px;
  }

  .mapping-activity-field {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    font-size: 12px;
    color: var(--color-text-base, #2e2e2e);
  }

  .mapping-activity-count {
    align-self: flex-end;
    font-size: 11px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .mapping-activity-remove {
    margin-bottom: 18px;
    padding: 0;
    border: none;
    background: none;
    color: var(--color-primary, #41aaaf);
    cursor: pointer;
    font-size: 13px;
    white-space: nowrap;
  }

  .entity-title-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 30px;
  }

  .entity-title {
    font-size: 16px;
    font-weight: 500;
    font-family: var(--font-family, Inter, sans-serif);
    line-height: 24px;
    color: var(--color-text-base, #2e2e2e);
  }

  .mapping-details-wrapper {
    display: flex;
    gap: 40px;
    //justify-content: space-between;
    //max-width: 60%;
  }

  .mapping-details {
    display: flex;
    font-size: 12px;
    line-height: 20px;
    font-family: var(--font-family, Inter, sans-serif);
    text-align: left;
    align-items: center;
    gap: 10px;
    color: var(--color-text-base, #2e2e2e);
  }

  .details-separator {
    border: 1px solid var(--color-primary, #41aaaf);
    height: 90%;
    background: var(--color-primary, #41aaaf);
  }

  .mapping-section-controls {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    font-size: 13px;
  }

  .mapping-section-controls--split {
    justify-content: space-between;
  }

  .mapping-section-controls--split > span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .mapping-section-control-link {
    background: none;
    border: none;
    padding: 0;
    color: var(--color-primary, #41aaaf);
    cursor: pointer;
    font-size: 13px;
    font-family: var(--font-family, Inter, sans-serif);

    &:hover {
      text-decoration: underline;
    }
  }

  .mapping-section-control-divider {
    color: var(--color-text-muted, #879494);
  }

  .mapping-sections-collapse {
    background: transparent;
    border: none;

    > .ant-collapse-item {
      margin-bottom: 12px;
      background: var(--color-surface-raised, #ffffff);
      border: 1px solid var(--color-border, #dbeae3) !important;
      border-radius: 10px !important;
      overflow: hidden;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .ant-collapse-header {
      padding: 16px 20px !important;
      align-items: center !important;
    }

    .ant-collapse-content-box {
      padding: 0 20px 20px !important;
    }
  }

  .mapping-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding-right: 8px;
  }

  .mapping-section-title {
    font-size: 20px;
    font-weight: 500;
    font-family: var(--font-family, Inter, sans-serif);
    line-height: 24px;
    text-align: left;
    color: var(--color-text, #042a2a);
  }

  .mapping-tax-rate-type {
    margin-top: 40px;
  }

  .mapping-section-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 400;
    white-space: nowrap;
  }

  .mapping-section-status-completed {
    color: #41aaaf;
  }

  .mapping-section-status-pending {
    color: var(--color-text-muted, #879494);
    font-style: italic;
  }

  .mapping-section-oos-action {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
  }

  .mapping-section-heading {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 12px;
    min-width: 0;
  }

  .mapping-section-header--sheet .mapping-section-title {
    font-size: 16px;
    font-weight: 600;
  }

  .mapping-section-sheet {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-text-muted, #5a6a6a);
    white-space: nowrap;
  }

  .mapping-section-account-count {
    margin-left: auto;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-status-success, #058181);
    white-space: nowrap;
  }
`;

export const MAPPING_TUTORIAL_HIDE = 'mapping-tutorial-hide';
export const TIFD_MAPPING_TUTORIAL_HIDE = 'tifd-mapping-tutorial-hide';

const MapWithAIButton = styled(ScopeButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  width: auto;
  height: 35px;
  padding: 2px 12px;
`;

interface MapTaxRatesProps {
  sections?: Section[];
  taxRateOptions?: TaxRateOption[];
  outOfScopeOptions?: TaxRateOption[];
  outOfScopeCode?: string;
  handleTaxRangeChange: (
    rateIds: string[],
    outOfScope: boolean,
    code: string
  ) => void;
  form: any;
  mappingData?: MappingData;
  taxRates?: TaxRate[];
  updateSelectedRates: (mappedData: SelectedRate[]) => void;
  setOptions: (trOptions: TaxRateOption[], oosOptions: TaxRateOption[]) => void;
  prepareTaxRateOptions: () => TaxRateOption[];
  addUnmapped: () => void;
  useCashReverse: boolean;
  selectedRates?: SelectedRate[];
  updateTaxRateType: (type: TaxRateType, rateId: string | any[]) => void;
  //disabled?: boolean;
  stepOneData: StepOneData;
  editMode?: boolean;
  useCashAccounting: boolean;
  editInitialData: () => void;
  hasTemplates: boolean;
  openTemplateWindow: () => void;
  /** Malta TIFD does not load a mapping from another entity. */
  includeLoadFromEntity?: boolean;
  openDefaultMappingsWindow: () => void;
  /** Bumps when Reset clears the mapping, so the account filter starts fresh. */
  ratesResetKey?: number;
  useReverseCharge: boolean;
  defaultTaxRatesData?: any;
  handlePreferences: (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => any;
  mapWithAI: () => void;
  aiMappingLoading?: boolean;
  includeCashAndReverseCharge?: boolean;
  showSectionSheet?: boolean;
  /** Malta TIFD does not classify accounts as input or output. */
  includeInputOutput?: boolean;
  tifdActivities?: TifdActivityDraft[];
  onTifdActivitiesChange?: (activities: TifdActivityDraft[]) => void;
}

export const SYSTEM_GENERATED = 'SYSTEM_GENERATED';
export const MapTaxRates: React.FC<MapTaxRatesProps> = ({
  sections,
  taxRateOptions,
  outOfScopeOptions,
  outOfScopeCode,
  handleTaxRangeChange,
  form,
  mappingData,
  taxRates,
  updateSelectedRates,
  setOptions,
  prepareTaxRateOptions,
  addUnmapped,
  useCashReverse,
  selectedRates,
  updateTaxRateType,
  //disabled,
  stepOneData,
  editMode = false,
  useCashAccounting,
  editInitialData,
  hasTemplates,
  openTemplateWindow,
  includeLoadFromEntity = true,
  openDefaultMappingsWindow,
  ratesResetKey = 0,
  useReverseCharge,
  defaultTaxRatesData,
  handlePreferences,
  mapWithAI,
  aiMappingLoading = false,
  includeCashAndReverseCharge = true,
  showSectionSheet = false,
  includeInputOutput = true,
  tifdActivities,
  onTifdActivitiesChange,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const activityDraftsRef = useRef<TifdActivityDraft[]>(tifdActivities ?? []);

  useEffect(() => {
    if (tifdActivities) activityDraftsRef.current = tifdActivities;
  }, [tifdActivities]);

  const commitActivities = (next: TifdActivityDraft[]) => {
    activityDraftsRef.current = next;
    writeTifdActivityDrafts(next);
    onTifdActivitiesChange?.(next);
  };

  const updateActivityField = (
    key: string,
    field: 'establishmentName' | 'operationDescription',
    value: string
  ) => {
    commitActivities(
      activityDraftsRef.current.map((row) =>
        row.key === key
          ? { ...row, [field]: value.slice(0, TIFD_ACTIVITY_FIELD_MAX) }
          : row
      )
    );
  };

  const providerKey = stepOneData.providerName?.toLowerCase();
  const isGroupProvider = providerKey === Provider.GROUP;

  const renderEntityProviderLogo = () => {
    if (!stepOneData.providerName) return null;

    const logoContent = isGroupProvider ? (
      <UsergroupAddOutlined
        style={{
          fontSize: 20,
          color: 'var(--color-text-base, #2e2e2e)',
          display: 'block',
        }}
      />
    ) : (
      <ProviderIcon
        providerName={stepOneData.providerName}
        alt={stepOneData.providerName}
        width={25}
        style={{ display: 'block' }}
      />
    );

    if (!logoContent) return null;

    return (
      <ProviderLogoNav
        providerName={stepOneData.providerName}
        shortCode={stepOneData.shortCode}
        organisationId={stepOneData.organisationId}
        style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
      >
        {logoContent}
      </ProviderLogoNav>
    );
  };

  const isTifdMapping = isMaltaTifdReturnType(mappingData?.typeId);
  const mappingTutorialHide = isTifdMapping
    ? TIFD_MAPPING_TUTORIAL_HIDE
    : MAPPING_TUTORIAL_HIDE;
  const mappingPanelHide = handlePreferences(
    PreferenceSections.MAPPING,
    PreferenceActions.GET,
    {
      name: mappingTutorialHide,
    }
  );

  const [showCard, setShowCard] = React.useState(!mappingPanelHide);
  const tifdGuideSteps = useMemo<MappingGuideStep[]>(
    () => [
      {
        title: t('returns.mapping.guide.tifd-map-accounts-title'),
        bullets: [
          t('returns.mapping.guide.tifd-map-accounts-step-1'),
          t('returns.mapping.guide.tifd-map-accounts-step-2'),
          t('returns.mapping.guide.tifd-map-accounts-step-3'),
        ],
        image: TifdMappingStepOne,
        alt: t('other.tifd-mapping-step-1-alt'),
      },
      {
        title: t('returns.mapping.guide.tifd-add-activities-title'),
        bullets: [
          t('returns.mapping.guide.tifd-add-activities-step-1'),
          t('returns.mapping.guide.tifd-add-activities-step-2'),
          t('returns.mapping.guide.tifd-add-activities-step-3'),
        ],
        image: TifdMappingStepActivities,
        alt: t('other.tifd-mapping-step-2-alt'),
      },
      {
        title: t('returns.mapping.guide.tifd-activity-credit-title'),
        bullets: [
          t('returns.mapping.guide.tifd-activity-credit-step-1'),
          t('returns.mapping.guide.tifd-activity-credit-step-2'),
          t('returns.mapping.guide.tifd-activity-credit-step-3'),
        ],
        image: TifdMappingStepTwo,
        alt: t('other.tifd-mapping-step-3-alt'),
      },
      {
        title: t('returns.mapping.guide.tifd-review-title'),
        bullets: [
          t('returns.mapping.guide.tifd-review-step-1'),
          t('returns.mapping.guide.tifd-review-step-2'),
          t('returns.mapping.guide.tifd-review-step-3'),
        ],
        image: TifdMappingStepThree,
        alt: t('other.tifd-mapping-step-4-alt'),
      },
    ],
    [t]
  );
  const specialCodes = React.useMemo(() => {
    return (
      sections
        ?.flatMap((s) => s.subsections)
        .filter((ss) => ss.outOfScope)
        .map((ss) => ss.code) ?? []
    );
  }, [sections]);

  const isOssMapping = isOssReturnType(mappingData?.typeId);
  const [onlyLinesWithAccounts, setOnlyLinesWithAccounts] = useState(false);
  const seenRatesResetKey = useRef(ratesResetKey);
  const skipFormLocationSyncRef = useRef(false);

  useEffect(() => {
    if (ratesResetKey === seenRatesResetKey.current) return;
    seenRatesResetKey.current = ratesResetKey;
    skipFormLocationSyncRef.current = true;
    setOnlyLinesWithAccounts(false);
  }, [ratesResetKey]);

  const activityNames = useMemo(
    () =>
      (tifdActivities ?? [])
        .map((activity) => tifdActivityText(activity.establishmentName))
        .filter(Boolean),
    [tifdActivities]
  );
  const canCollapseActivities = activityNames.length > 0;
  const [activitiesExpanded, setActivitiesExpanded] = useState(true);
  const appliedSavedActivitiesCollapse = useRef(false);

  useEffect(() => {
    if (!isTifdMapping) return;
    if (!canCollapseActivities) {
      setActivitiesExpanded(true);
      return;
    }
    if (!editMode || appliedSavedActivitiesCollapse.current) return;
    appliedSavedActivitiesCollapse.current = true;
    setActivitiesExpanded(false);
  }, [canCollapseActivities, editMode, isTifdMapping]);

  useEffect(() => {
    if (skipFormLocationSyncRef.current) {
      skipFormLocationSyncRef.current = false;
      return;
    }
    if (!isTifdMapping || !form || !selectedRates?.length) return;
    const fields = form.getFieldsValue(true) as Record<string, unknown>;
    const codesByAccount = new Map<string, string[]>();
    Object.entries(fields).forEach(([field, raw]) => {
      if (!Array.isArray(raw)) return;
      const code = field === 'outOfScope' ? outOfScopeCode : field;
      if (!code) return;
      raw.forEach((id) => {
        if (typeof id !== 'string' && typeof id !== 'number') return;
        const key = String(id);
        const codes = codesByAccount.get(key) ?? [];
        if (!codes.includes(code)) codes.push(code);
        codesByAccount.set(key, codes);
      });
    });
    if (codesByAccount.size === 0) return;
    let changed = false;
    const next = selectedRates.map((rate) => {
      const fromForm = codesByAccount.get(String(rate.value));
      if (!fromForm?.length || rate.location?.length) return rate;
      changed = true;
      return { ...rate, location: [fromForm[0]] };
    });
    if (changed) updateSelectedRates(next);
  }, [form, isTifdMapping, outOfScopeCode, selectedRates, updateSelectedRates]);

  const collapsibleSections = useMemo(
    () =>
      sections?.filter(
        (section) => section.name !== section.subsections[0]?.code
      ) ?? [],
    [sections]
  );

  const sectionIdsKey = collapsibleSections
    .map((section) => section.id)
    .join(',');

  const getDefaultActiveSectionKeys = useCallback(
    (sectionList: Section[]) => {
      if (sectionList.length === 0) return [];
      if (isOssMapping || isTifdMapping) return [sectionList[0].id];
      return sectionList.map((section) => section.id);
    },
    [isOssMapping, isTifdMapping]
  );

  const [activeSectionKeys, setActiveSectionKeys] = useState<string[]>(() =>
    getDefaultActiveSectionKeys(collapsibleSections)
  );

  useEffect(() => {
    const nextKeys = getDefaultActiveSectionKeys(collapsibleSections);
    setActiveSectionKeys((current) => {
      if (
        current.length === nextKeys.length &&
        current.every((key, index) => key === nextKeys[index])
      ) {
        return current;
      }
      return nextKeys;
    });
  }, [sectionIdsKey, getDefaultActiveSectionKeys]);

  const mappedSubsectionCodes = useMemo(
    () => getMappedSubsectionCodesFromSelectedRates(selectedRates),
    [selectedRates]
  );

  const lineHasAccounts = useCallback(
    (code: string) =>
      (selectedRates ?? []).some((rate) => rate.location?.includes(code)),
    [selectedRates]
  );

  const visibleSections = useMemo(() => {
    if (!isTifdMapping || !onlyLinesWithAccounts) return collapsibleSections;
    return collapsibleSections.filter((section) =>
      section.subsections.some((subsection) => lineHasAccounts(subsection.code))
    );
  }, [
    collapsibleSections,
    isTifdMapping,
    lineHasAccounts,
    onlyLinesWithAccounts,
  ]);

  const expandAllSections = () => {
    setActiveSectionKeys(visibleSections.map((section) => section.id));
  };

  const collapseAllSections = () => {
    setActiveSectionKeys([]);
  };

  const getSpecialFieldName = useCallback(
    (code: string) => (code === outOfScopeCode ? 'outOfScope' : code),
    [outOfScopeCode]
  );

  /** Full option list (stable identity when parent keeps taxRateOptions/outOfScopeOptions stable). */
  const allTaxRateOptions = useMemo(
    () => taxRateOptions ?? outOfScopeOptions ?? [],
    [taxRateOptions, outOfScopeOptions]
  );

  /**
   * Regular box options: hide rates that are only mapped to special (OOS/Other) boxes.
   * Matches previous taxRateOptions splice behavior on OOS changes.
   */
  const specialOnlyRateIdsKey = useMemo(() => {
    if (!selectedRates?.length) return '';
    return selectedRates
      .filter(
        (sr) =>
          !!sr.location?.length &&
          sr.location.every((loc) => specialCodes.includes(loc))
      )
      .map((sr) => sr.value)
      .sort()
      .join(',');
  }, [selectedRates, specialCodes]);

  const regularTaxRateOptions = useMemo(() => {
    if (!specialOnlyRateIdsKey) return allTaxRateOptions;
    const specialOnlyIds = new Set(specialOnlyRateIdsKey.split(','));
    return allTaxRateOptions.filter((opt) => !specialOnlyIds.has(opt.value));
  }, [allTaxRateOptions, specialOnlyRateIdsKey]);

  /**
   * Base options for special boxes: hide rates whose primary location is a regular box.
   * Matches previous outOfScopeOptions splice on regular-box changes.
   */
  const regularMappedRateIdsKey = useMemo(() => {
    if (!selectedRates?.length) return '';
    return selectedRates
      .filter(
        (sr) =>
          sr.location?.length > 0 && !specialCodes.includes(sr.location[0])
      )
      .map((sr) => sr.value)
      .sort()
      .join(',');
  }, [selectedRates, specialCodes]);

  const specialBaseOptions = useMemo(() => {
    if (!regularMappedRateIdsKey) return allTaxRateOptions;
    const regularMappedIds = new Set(regularMappedRateIdsKey.split(','));
    return allTaxRateOptions.filter((opt) => !regularMappedIds.has(opt.value));
  }, [allTaxRateOptions, regularMappedRateIdsKey]);

  /** Per special-box blocked rate ids (mutual exclusivity between OOS / Other). */
  const specialBlockedIdsKeyByCode = useMemo(() => {
    const result: Record<string, string> = {};
    for (const code of specialCodes) {
      const isOutOfScopeBox = code === outOfScopeCode;
      const blocked = (selectedRates ?? [])
        .filter((sr) => {
          const loc = sr.location?.[0];
          if (!loc || !specialCodes.includes(loc)) return false;
          return isOutOfScopeBox
            ? loc !== outOfScopeCode
            : loc === outOfScopeCode;
        })
        .map((sr) => sr.value)
        .sort()
        .join(',');
      result[code] = blocked;
    }
    return result;
  }, [selectedRates, specialCodes, outOfScopeCode]);

  const specialOptionsByCode = useMemo(() => {
    const map = new Map<string, TaxRateOption[]>();
    for (const code of specialCodes) {
      const blockedKey = specialBlockedIdsKeyByCode[code] ?? '';
      if (!blockedKey) {
        map.set(code, specialBaseOptions);
        continue;
      }
      const blockedIds = new Set(blockedKey.split(','));
      map.set(
        code,
        specialBaseOptions.filter((opt) => !blockedIds.has(opt.value))
      );
    }
    return map;
  }, [specialCodes, specialBaseOptions, specialBlockedIdsKeyByCode]);

  /**
   * TIFD: an account mapped to one box is hidden from every other box.
   * The box that already has it keeps it, so the selected tag still renders.
   */
  const tifdOptionsByCode = useMemo(() => {
    if (!isTifdMapping) return null;
    const usedByCode = new Map<string, Set<string>>();
    (selectedRates ?? []).forEach((rate) => {
      const accountId = String(rate.value);
      rate.location?.forEach((loc) => {
        if (!loc) return;
        const ids = usedByCode.get(loc) ?? new Set<string>();
        ids.add(accountId);
        usedByCode.set(loc, ids);
      });
    });
    const usedAnywhere = new Set<string>();
    usedByCode.forEach((ids) => ids.forEach((id) => usedAnywhere.add(id)));

    const map = new Map<string, TaxRateOption[]>();
    sections?.forEach((section) => {
      section.subsections.forEach((subsection) => {
        const base = subsection.outOfScope
          ? specialOptionsByCode.get(subsection.code) ?? specialBaseOptions
          : regularTaxRateOptions;
        const keep = usedByCode.get(subsection.code);
        map.set(
          subsection.code,
          usedAnywhere.size === 0
            ? base
            : base.filter((option) => {
                const id = String(option.value);
                return keep?.has(id) || !usedAnywhere.has(id);
              })
        );
      });
    });
    return map;
  }, [
    isTifdMapping,
    regularTaxRateOptions,
    sections,
    selectedRates,
    specialBaseOptions,
    specialOptionsByCode,
  ]);

  const syncSpecialSelections = useCallback(
    (selectedIds: string[], selectedCode: string) => {
      if (!sections || !form) return;

      const codes = sections
        .flatMap((s) => s.subsections)
        .filter((ss) => ss.outOfScope)
        .map((ss) => ss.code);

      const nextFields: Record<string, string[]> = {};
      const currentField = getSpecialFieldName(selectedCode);

      nextFields[currentField] = selectedIds;

      codes
        .filter((code) => code !== selectedCode)
        .forEach((code) => {
          const fieldName = getSpecialFieldName(code);
          const existing = (form.getFieldValue(fieldName) || []) as string[];
          nextFields[fieldName] = existing.filter(
            (value) => !selectedIds.includes(value)
          );
        });

      form.setFieldsValue(nextFields);
    },
    [sections, form, getSpecialFieldName]
  );

  const handleTaxRangeChangeRef = useRef(handleTaxRangeChange);
  handleTaxRangeChangeRef.current = handleTaxRangeChange;

  const stableHandleTaxRangeChange = useCallback(
    (rateIds: string[], outOfScope: boolean, code: string) => {
      handleTaxRangeChangeRef.current(rateIds, outOfScope, code);
    },
    []
  );

  useEffect(() => {
    let isMounted = true;

    if (mappingData?.templateMappingId) {
      const fullOpts: TaxRateOption[] = prepareTaxRateOptions();
      const optionValues = new Set(fullOpts.map((opt) => opt.value));

      const md: SelectedRate[] = [];
      const oosCode =
        outOfScopeCode ??
        sections?.find((s) => s.subsections[0].outOfScope)?.subsections[0].code;
      const caCode = includeCashAndReverseCharge
        ? sections?.find((s) => s.name === s.subsections[0].code)?.name
        : undefined;
      const jsonMapping = mappingData?.jsonMapping;
      const newValues = {};

      jsonMapping?.map((jm) => {
        const taxRateId = jm.taxType;
        const value = taxRateId ? taxRateId : '';
        if (optionValues.has(value)) {
          const codes = jm.codes?.split(',');
          const loc = codes ? codes : [];
          let cashAccounting = false;
          const reverseCharge: number | null =
            includeCashAndReverseCharge &&
            jm.reverseCharge !== null &&
            parseFloat(jm.reverseCharge) > 0
              ? parseFloat(jm.reverseCharge)
              : null;
          const taxRateType =
            includeInputOutput && jm.taxRateType
              ? jm.taxRateType
              : TaxRateType.INPUT;
          codes?.map((c) => {
            let boxCode = c;
            if (c === oosCode) {
              boxCode = 'outOfScope';
            }
            if (c === caCode) {
              cashAccounting = true;
            } else {
              if (Array.isArray(newValues[boxCode]))
                newValues[boxCode].push(taxRateId);
              else {
                newValues[boxCode] = [taxRateId];
              }
            }
          });
          const location = loc.filter((l) => l !== caCode);
          !!value &&
            md.push({
              value,
              location,
              cashAccounting,
              reverseCharge,
              taxRateType,
            });
        }
      });

      try {
        if (isMounted) {
          // Seed unmapped tax rates as empty — do not merge prior selectedRates,
          // or locations from another return type (e.g. OSS) can leak into this mapping.
          taxRates?.forEach((tr) => {
            if (md.findIndex((m) => m.value === tr.taxType) === -1) {
              md.push({
                value: tr.taxType,
                location: [],
                cashAccounting: false,
                reverseCharge: null,
                taxRateType: TaxRateType.INPUT,
              });
            }
          });
          if (jsonMapping && jsonMapping.length > 0) updateSelectedRates(md);
          // Keep full option lists; filtered options are derived from selectedRates.
          setOptions(fullOpts, fullOpts);
          form.resetFields();
          form.setFieldsValue(newValues);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [mappingData, includeCashAndReverseCharge, includeInputOutput]);

  const NOT_USED = 'NOT_USED';
  const [selectedKeys, setSelectedKeys] = useState<TransferProps['targetKeys']>(
    []
  );
  const transferContainerRef = useRef<HTMLDivElement>(null);

  const taxRateTypeData = useMemo(() => {
    if (!includeInputOutput || !selectedRates?.length) return [];
    return selectedRates
      .map((sr) => {
        const loc0 = sr.location?.[0];
        const isSpecialLocation = specialCodes.some(
          (c) => c?.toUpperCase() === loc0?.toUpperCase()
        );
        const notUsed = sr.location.length === 0 || isSpecialLocation;
        const title = taxRates?.find((tr) => tr.taxType === sr.value)?.name;
        return {
          key: sr.value,
          title: notUsed ? NOT_USED : title ? title : '',
          description: title && !notUsed ? title : '',
        } satisfies RecordType;
      })
      .filter((d) => d.title !== NOT_USED);
  }, [includeInputOutput, selectedRates, specialCodes, taxRates]);

  const targetKeys = useMemo(
    () =>
      taxRateTypeData
        .filter((item) => {
          const nIdx = selectedRates?.findIndex((sr) => sr.value === item.key);
          return (
            nIdx != null &&
            nIdx !== -1 &&
            selectedRates?.[nIdx].taxRateType === TaxRateType.OUTPUT
          );
        })
        .map((item) => item.key),
    [taxRateTypeData, selectedRates]
  );

  const labelTransferCheckboxes = useCallback(() => {
    const root = transferContainerRef.current;
    if (!root) return;

    const titleByKey = new Map(
      (taxRateTypeData ?? []).map((item) => [
        String(item.key),
        String(item.title ?? ''),
      ])
    );

    root.querySelectorAll('.ant-transfer-list-content-item').forEach((item) => {
      const input = item.querySelector('input[type="checkbox"]');
      if (!(input instanceof HTMLInputElement)) return;

      const textEl = item.querySelector('.ant-transfer-list-content-item-text');
      const fromDom = textEl?.textContent?.trim();
      const fromData = titleByKey.get(String(input.value));
      const label = fromDom || fromData;
      if (label) {
        input.setAttribute('aria-label', label);
      }
    });

    root.querySelectorAll('.ant-transfer-list').forEach((list, index) => {
      const headerCheckbox = list.querySelector(
        '.ant-transfer-list-header input[type="checkbox"]'
      );
      if (headerCheckbox instanceof HTMLInputElement) {
        headerCheckbox.setAttribute(
          'aria-label',
          index === 0
            ? `${t('common.select-all')} — ${t('returns.mapping.input')}`
            : `${t('common.select-all')} — ${t('returns.mapping.output')}`
        );
      }
    });
  }, [t, taxRateTypeData]);

  useLayoutEffect(() => {
    labelTransferCheckboxes();
  }, [labelTransferCheckboxes, taxRateTypeData, targetKeys, selectedKeys]);

  const onTaxRateTypeChange: TransferProps['onChange'] = (nextTargetKeys) => {
    updateTaxRateType(TaxRateType.OUTPUT, nextTargetKeys);
  };

  const onSelectChange: TransferProps['onSelectChange'] = (
    sourceSelectedKeys,
    targetSelectedKeys
  ) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  const formatTaxRateCount = (
    selectedCount: number,
    totalCount: number
  ): string => {
    if (selectedCount === 0) {
      return t('returns.mapping.tax-rate-count', { count: totalCount });
    }
    return t('returns.mapping.tax-rate-selected', {
      selectedCount,
      totalCount,
      count: totalCount,
    });
  };

  const renderTaxRateTypeSelection = () => {
    return (
      <div ref={transferContainerRef}>
        <Transfer
          dataSource={taxRateTypeData}
          titles={[t('returns.mapping.input'), t('returns.mapping.output')]}
          targetKeys={targetKeys}
          selectedKeys={selectedKeys}
          onChange={onTaxRateTypeChange}
          onSelectChange={onSelectChange}
          render={(item) => item.title}
          listStyle={{
            width: 400,
            height: 260,
          }}
          selectAllLabels={[
            ({ selectedCount, totalCount }) => (
              <span>{formatTaxRateCount(selectedCount, totalCount)}</span>
            ),
            ({ selectedCount, totalCount }) => (
              <span>{formatTaxRateCount(selectedCount, totalCount)}</span>
            ),
          ]}
        />
      </div>
    );
  };
  const renderTopButtons = () => {
    return (
      <Space>
        <ScopeButton
          width="fit-content"
          height="25px"
          fontSize="12px"
          lineheight="12px"
          onClick={guideVideoWindow}
          type="default"
        >
          {t('returns.mapping.take-interactive-tour')}
        </ScopeButton>
        <a
          href="https://generate.tax/book-a-session/"
          target="_blank"
          rel="noreferrer"
        >
          <ScopeButton
            width="fit-content"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            type="dark-orange"
          >
            {t('returns.mapping.book-onboarding-session')}
          </ScopeButton>
        </a>
      </Space>
    );
  };

  const guideVideoWindow = () => {
    const tourTitle = t('returns.mapping.take-interactive-tour');
    const modalData: ModalData = {
      type: WindowType.VIDEO,
      title: tourTitle,
      component: (
        <div
          style={{
            position: 'relative',
            paddingBottom: 'calc(47.46527777777778% + 41px)',
            height: 0,
            width: '100%',
          }}
        >
          <iframe
            src="https://demo.arcade.software/0vvpiO2YylrGctTGC7nI?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true"
            title={tourTitle}
            frameBorder="0"
            loading="lazy"
            allowFullScreen
            allow="clipboard-write"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              colorScheme: 'light',
            }}
          />
        </div>
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const renderSectionContent = (section: Section) => {
    const hasOutOfScopeSubsection =
      section.subsections.findIndex((subsection) => subsection.outOfScope) > -1;

    return (
      <>
        {hasOutOfScopeSubsection && (
          <div className="mapping-section-oos-action">
            <ScopeButton //@ts-ignore
              width="250px"
              height="30px"
              type={'default'}
              fontSize="12px"
              lineheight="12px"
              onClick={() => addUnmapped()}
              disabled={useCashReverse}
            >
              {t('returns.mapping.mark-remaining-oos')}
            </ScopeButton>
          </div>
        )}
        <table style={{ width: '100%' }}>
          <tbody>
            {(isTifdMapping && onlyLinesWithAccounts
              ? section.subsections.filter((subsection) =>
                  lineHasAccounts(subsection.code)
                )
              : section.subsections
            ).map((ss) => {
              const hintText = (ss.altHint || ss.hint || '').trim();
              const isOutOfScopeBox =
                !!ss.outOfScope && ss.code === outOfScopeCode;
              const isOtherSpecialBox = !!ss.outOfScope && !isOutOfScopeBox;
              const treatAsOutOfScope =
                !!ss.outOfScope &&
                (!outOfScopeCode || ss.code === outOfScopeCode);
              const fieldName =
                ss.outOfScope && (!outOfScopeCode || ss.code === outOfScopeCode)
                  ? 'outOfScope'
                  : ss.code;
              const options = isTifdMapping
                ? tifdOptionsByCode?.get(ss.code) ??
                  (ss.outOfScope
                    ? specialOptionsByCode.get(ss.code) ?? specialBaseOptions
                    : regularTaxRateOptions)
                : ss.outOfScope
                  ? specialOptionsByCode.get(ss.code) ?? specialBaseOptions
                  : regularTaxRateOptions;

              return (
                <MappingBoxSelectRow
                  key={ss.id}
                  subsectionId={ss.id}
                  subsectionName={ss.name}
                  subsectionCode={ss.code}
                  subsectionType={ss.type}
                  hintText={hintText}
                  fieldName={fieldName}
                  isOutOfScope={!!ss.outOfScope}
                  isOutOfScopeBox={isOutOfScopeBox}
                  isOtherSpecialBox={isOtherSpecialBox}
                  treatAsOutOfScope={treatAsOutOfScope}
                  options={options}
                  disabled={useCashReverse}
                  onRatesChange={stableHandleTaxRangeChange}
                  onSpecialSync={
                    ss.outOfScope ? syncSpecialSelections : undefined
                  }
                />
              );
            })}
          </tbody>
        </table>
      </>
    );
  };

  const sectionSheetLabel = (section: Section) => {
    const notOnReturn =
      section.subsections.length > 0 &&
      section.subsections.every((subsection) => subsection.outOfScope);
    if (notOnReturn) return t('returns.mapping.sheet-not-on-return');
    if (section.sheet === 'BALANCE_SHEET') {
      return t('returns.mapping.sheet-balance-sheet');
    }
    if (section.sheet === 'INCOME') return t('returns.mapping.sheet-income');
    if (!section.sheet) return '';
    return section.sheet.replace(/_/g, ' ');
  };

  const mappedAccountCount = (section: Section) => {
    const codes = new Set(
      section.subsections.map((subsection) => subsection.code)
    );
    return (selectedRates ?? []).filter((rate) =>
      rate.location.some((code) => codes.has(code))
    ).length;
  };

  const renderSectionHeader = (section: Section) => {
    const title = getMappingSectionLabel(section.name, t, i18n.language);

    if (showSectionSheet && isMaltaTifdReturnType(mappingData?.typeId)) {
      const accountCount = mappedAccountCount(section);
      const sheetLabel = sectionSheetLabel(section);
      return (
        <div className="mapping-section-header mapping-section-header--sheet">
          <span className="mapping-section-heading">
            <span className="mapping-section-title">{title}</span>
            {sheetLabel ? (
              <span className="mapping-section-sheet">{sheetLabel}</span>
            ) : null}
          </span>
          {accountCount > 0 ? (
            <span className="mapping-section-account-count">
              {t('returns.mapping.section-account-count', {
                count: accountCount,
              })}
            </span>
          ) : null}
        </div>
      );
    }

    const isCompleted = isMappingSectionCompleted(
      section,
      mappedSubsectionCodes
    );

    return (
      <div className="mapping-section-header">
        <span className="mapping-section-title">{title}</span>
        <span
          className={`mapping-section-status ${
            isCompleted
              ? 'mapping-section-status-completed'
              : 'mapping-section-status-pending'
          }`}
        >
          {isCompleted ? (
            <>
              <CheckCircleOutlined />
              {t('returns.mapping.section-completed')}
            </>
          ) : (
            t('returns.mapping.section-pending')
          )}
        </span>
      </div>
    );
  };

  // First paint keeps section headers only; dropdown bodies follow in a
  // lower-priority render so a long chart does not block the page open.
  const deferredActiveKeys = useDeferredValue(activeSectionKeys, []);

  const sectionCollapseItems = visibleSections.map((section) => ({
    key: section.id,
    label: renderSectionHeader(section),
    // Only build Select trees for expanded sections — avoids lag on long OSS forms.
    children: deferredActiveKeys.includes(section.id)
      ? renderSectionContent(section)
      : null,
  }));

  return (
    <>
      <MapTaxRatesWrapper className="map-tax-rates-wrapper">
        {pwaMobileNav ? (
          <Row justify={'end'} style={{ marginTop: -20, marginBottom: 20 }}>
            {renderTopButtons()}
          </Row>
        ) : showCard ? (
          <MappingGuide
            setShowCard={setShowCard}
            handlePreferences={handlePreferences}
            renderTopButtons={renderTopButtons}
            steps={isTifdMapping ? tifdGuideSteps : undefined}
            hidePreferenceName={mappingTutorialHide}
          />
        ) : (
          <Row
            justify={'space-between'}
            style={{ marginTop: -20, marginBottom: 20 }}
          >
            {renderTopButtons()}
            <ExportButton
              type="text"
              onClick={() => {
                setShowCard(true);
                handlePreferences(
                  PreferenceSections.MAPPING,
                  PreferenceActions.SET,
                  {
                    name: mappingTutorialHide,
                    value: false,
                  }
                );
              }}
              icon={<IconSupport style={{ width: 20, height: 20 }} />} //@ts-ignore
              isGuide
            >
              {t('organisation.open-guide')}
            </ExportButton>
          </Row>
        )}
        <div className="mapping-details-box">
          <div className="entity-title-row">
            <Space align="center">
              {renderEntityProviderLogo()}
              <span className="entity-title">{stepOneData.entity}</span>
              {!useCashAccounting && (
                <ExportButton onClick={() => editInitialData()}>
                  {t('returns.mapping.edit-period')}
                </ExportButton>
              )}
            </Space>
            <Space>
              {defaultTaxRatesData?.defaultTaxRates &&
                defaultTaxRatesData.defaultTaxRates.length > 0 && (
                  <Tooltip title={t('returns.mapping.load-defaults-tooltip')}>
                    <ExportButton
                      onClick={openDefaultMappingsWindow}
                      disabled={useCashAccounting || useReverseCharge}
                      icon={<IconImport style={{ width: 20, height: 20 }} />}
                    >
                      <span>{t('returns.mapping.load-defaults')}</span>
                    </ExportButton>
                  </Tooltip>
                )}
              {includeLoadFromEntity ? (
                <Tooltip
                  title={
                    !hasTemplates
                      ? t('returns.mapping.no-templates-tooltip')
                      : ''
                  }
                >
                  <ExportButton
                    onClick={openTemplateWindow}
                    disabled={
                      !hasTemplates || useCashAccounting || useReverseCharge
                    }
                    icon={<IconImport style={{ width: 20, height: 20 }} />}
                  >
                    <span>{t('returns.mapping.load-from-another-entity')}</span>
                  </ExportButton>
                </Tooltip>
              ) : null}

              <Tooltip title={''}>
                <FeatureDotBadge dotId="returns-map-with-ai">
                  <MapWithAIButton
                    type="dark-orange"
                    onClick={mapWithAI}
                    disabled={aiMappingLoading}
                    loading={aiMappingLoading}
                  >
                    <IconFuzzyMapping style={{ width: 18, height: 18 }} />
                    {t('returns.mapping.try-mapping-with-ai')}
                  </MapWithAIButton>
                </FeatureDotBadge>
              </Tooltip>
            </Space>
          </div>

          <div className="mapping-details-wrapper">
            <div className="mapping-details">
              <div className="details-separator" />
              <span>
                <strong>{t('returns.mapping.tax-return-label')}</strong>
                <br />
                {stepOneData.country} - {stepOneData.typeName}
              </span>
            </div>

            <div className="mapping-details">
              <div className="details-separator" />
              <span>
                <strong>{t('submissions.period-label')}</strong>
                <br />
                {stepOneData.firstPeriod}
              </span>
            </div>

            <div className="mapping-details">
              <div className="details-separator" />
              <span>
                <strong>{t('returns.mapping.due-date-label')}</strong>
                <br />
                {formatDisplayDate(
                  stepOneData.firstDueDate,
                  'DD/MM/YYYY',
                  i18n.language
                )}
              </span>
            </div>
          </div>
        </div>
        {isTifdMapping && tifdActivities && onTifdActivitiesChange ? (
          <Collapse
            className="mapping-activities-collapse"
            bordered={false}
            expandIconPosition="end"
            expandIcon={canCollapseActivities ? undefined : () => null}
            collapsible={canCollapseActivities ? 'header' : 'disabled'}
            activeKey={activitiesExpanded ? ['activities'] : []}
            onChange={(keys) => {
              if (!canCollapseActivities) {
                setActivitiesExpanded(true);
                return;
              }
              const next = Array.isArray(keys) ? keys : keys ? [keys] : [];
              setActivitiesExpanded(next.includes('activities'));
            }}
            items={[
              {
                key: 'activities',
                label: (
                  <div className="mapping-activities-header">
                    <span className="mapping-activities-title">
                      {t('returns.mapping.activities-title')}
                    </span>
                    {!activitiesExpanded && activityNames.length > 0 ? (
                      <span className="mapping-activities-names">
                        - {activityNames.join(', ')}
                      </span>
                    ) : null}
                  </div>
                ),
                children: (
                  <>
                    <p className="mapping-activities-description">
                      {t('returns.mapping.activities-description')}
                    </p>
                    {tifdActivities.map((activity) => (
                      <div className="mapping-activity-row" key={activity.key}>
                        <label className="mapping-activity-field">
                          {t('returns.mapping.activity-establishment-name')}
                          <Input
                            maxLength={TIFD_ACTIVITY_FIELD_MAX}
                            value={activity.establishmentName}
                            onChange={(event) =>
                              updateActivityField(
                                activity.key,
                                'establishmentName',
                                event.target.value
                              )
                            }
                          />
                          <span className="mapping-activity-count">
                            {activity.establishmentName.length}/
                            {TIFD_ACTIVITY_FIELD_MAX}
                          </span>
                        </label>
                        <label className="mapping-activity-field">
                          {t('returns.mapping.activity-operation-description')}
                          <Input
                            maxLength={TIFD_ACTIVITY_FIELD_MAX}
                            value={activity.operationDescription}
                            onChange={(event) =>
                              updateActivityField(
                                activity.key,
                                'operationDescription',
                                event.target.value
                              )
                            }
                          />
                          <span className="mapping-activity-count">
                            {activity.operationDescription.length}/
                            {TIFD_ACTIVITY_FIELD_MAX}
                          </span>
                        </label>
                        {tifdActivities.length > 1 ? (
                          <button
                            type="button"
                            className="mapping-activity-remove"
                            onClick={() =>
                              commitActivities(
                                activityDraftsRef.current.filter(
                                  (row) => row.key !== activity.key
                                )
                              )
                            }
                          >
                            {t('returns.mapping.remove-activity')}
                          </button>
                        ) : null}
                      </div>
                    ))}
                    <ScopeButton
                      type="default"
                      width="fit-content"
                      height="32px"
                      fontSize="13px"
                      lineheight="13px"
                      disabled={tifdActivities.length >= TIFD_ACTIVITY_LIMIT}
                      onClick={() =>
                        commitActivities([
                          ...activityDraftsRef.current,
                          createTifdActivityDraft(),
                        ])
                      }
                    >
                      {t('returns.mapping.add-activity')}
                    </ScopeButton>
                  </>
                ),
              },
            ]}
          />
        ) : null}
        <Form name="basic" form={form} autoComplete="off">
          {collapsibleSections.length > 0 && (
            <div
              className={`mapping-section-controls${
                isTifdMapping ? ' mapping-section-controls--split' : ''
              }`}
            >
              {isTifdMapping ? (
                <Checkbox
                  checked={onlyLinesWithAccounts}
                  onChange={(event) =>
                    setOnlyLinesWithAccounts(event.target.checked)
                  }
                >
                  {t('returns.mapping.only-lines-with-accounts')}
                </Checkbox>
              ) : null}
              <span>
                <button
                  type="button"
                  className="mapping-section-control-link"
                  onClick={expandAllSections}
                >
                  {t('returns.mapping.expand-all-sections')}
                </button>
                <span className="mapping-section-control-divider">|</span>
                <button
                  type="button"
                  className="mapping-section-control-link"
                  onClick={collapseAllSections}
                >
                  {t('returns.mapping.collapse-all-sections')}
                </button>
              </span>
            </div>
          )}
          {collapsibleSections.length > 0 && (
            <Collapse
              className="mapping-sections-collapse"
              bordered={false}
              expandIconPosition="end"
              activeKey={activeSectionKeys}
              onChange={(keys) =>
                setActiveSectionKeys(
                  Array.isArray(keys) ? keys : keys ? [keys] : []
                )
              }
              destroyOnHidden
              items={sectionCollapseItems}
            />
          )}
          {includeInputOutput && (
            <Row className="mapping-tax-rate-type">
              <Col
                span={24}
                className="mapping-section"
                style={{ flexDirection: 'column' }}
              >
                <span>{t('returns.mapping.tax-rate-type')}</span>
                <div className="divider-10" />
                <span className="mapping-label">
                  {t('returns.mapping.tax-rate-type-description')}
                </span>
                <div className="divider-10" />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    //alignItems: 'center',
                  }}
                >
                  {renderTaxRateTypeSelection()}
                </div>
              </Col>
            </Row>
          )}
        </Form>
      </MapTaxRatesWrapper>
    </>
  );
};
